import { css } from '@emotion/core'
import React, { useEffect } from 'react'
import { Button, Cell, Grid, IconButton, InputGroup, Row, TextField, H5, DataBlock } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { PersonFamily } from '@fs/zion-icon'
import { PersonLink, TreePersonLink } from '../../../app/Links'
import { useFamilyDetailsState } from './useFamilyDetailsState'
import { RawData } from '../../../common/RawData'
import { LabelsBlock, OriginalAndStandardFactBlock } from '../../commonComponents/CommonComponents'

export default () => {}

export function FamilyDetailsView({
  buttonTemplates,
  backToMessagesList,
  showViewButton,
  showButtonsJustPrimary,
  personLinkInNewTab,
  goBackAfterButtonCallback,
  showDeleteMessageButton,
}) {
  const { t } = useTranslation()
  const { familyDetailsState, familyDetailsActions } = useFamilyDetailsState(
    backToMessagesList,
    showViewButton,
    showButtonsJustPrimary,
    personLinkInNewTab,
    goBackAfterButtonCallback,
    showDeleteMessageButton
  )

  useEffect(() => {
    familyDetailsActions.load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!familyDetailsState.queueFamily) {
    return null
  }

  return (
    <Grid>
      {/* Row */}
      <Cell columns={3}>
        <InputGroup label={'\xa0'}>
          <Button onClick={() => backToMessagesList()}>{t('queues.backToMessages', 'Back To Messages')}</Button>
        </InputGroup>
      </Cell>
      <Cell columns={3}>
        <Row>
          <TextField
            label={t('queues.enterPersonId', 'Enter a Person ID')}
            value={familyDetailsState.principalPersonId}
            onChange={(event) => familyDetailsActions.setPrincipalPersonId(event.target.value)}
          />
          <InputGroup label={'\xa0'}>
            <Button
              emphasis="medium"
              onClick={() => familyDetailsActions.lookForFamily(familyDetailsState.principalPersonId)}
            >
              View
            </Button>
          </InputGroup>
        </Row>
      </Cell>
      <Cell columns={5}>
        {familyDetailsState.showDeleteMessageButton && (
          <Row alignX="end">
            <InputGroup label={'\xa0'}>
              <Button emphasis="low" onClick={() => familyDetailsActions.deleteMessage()}>
                Delete Message
              </Button>
            </InputGroup>
          </Row>
        )}
      </Cell>
      <p />
      {/* Row */}
      <Cell columns={12}>
        <H5>{t('principal', 'Principal')}</H5>
      </Cell>
      {/* Row */}
      <Cell columns={12}>
        <PersonDetailsView
          person={familyDetailsState.queueFamily.principal}
          buttonTemplates={buttonTemplates}
          familyDetailsState={familyDetailsState}
          familyDetailsActions={familyDetailsActions}
        />
      </Cell>
      {familyDetailsState.queueFamily.spouses.length > 0 && (
        <>
          {/* Row */}
          <Cell columns={12}>
            <H5>{t('spouse', 'Spouse')}</H5>
          </Cell>

          {/* Row */}
          <Cell columns={12}>
            {familyDetailsState.queueFamily.spouses.map((spouse) => {
              return (
                <PersonDetailsView
                  key={spouse.personId}
                  person={spouse}
                  buttonTemplates={buttonTemplates}
                  familyDetailsState={familyDetailsState}
                  familyDetailsActions={familyDetailsActions}
                />
              )
            })}
          </Cell>
        </>
      )}
      {familyDetailsState.queueFamily.parents1.length > 0 && (
        <>
          {/* Row */}
          <Cell columns={12}>
            <H5>{t('parent1', 'Parent 1')}</H5>
          </Cell>

          {/* Row */}
          <Cell columns={12}>
            {familyDetailsState.queueFamily.parents1.map((parent1) => {
              return (
                <PersonDetailsView
                  key={parent1.personId}
                  person={parent1}
                  buttonTemplates={buttonTemplates}
                  familyDetailsState={familyDetailsState}
                  familyDetailsActions={familyDetailsActions}
                />
              )
            })}
          </Cell>
        </>
      )}
      {familyDetailsState.queueFamily.parents2.length > 0 && (
        <>
          {/* Row */}
          <Cell columns={12}>
            <H5>{t('parent2', 'Parent 2')}</H5>
          </Cell>

          {/* Row */}
          <Cell columns={12}>
            {familyDetailsState.queueFamily.parents2.map((parent2) => {
              return (
                <PersonDetailsView
                  key={parent2.personId}
                  person={parent2}
                  buttonTemplates={buttonTemplates}
                  familyDetailsState={familyDetailsState}
                  familyDetailsActions={familyDetailsActions}
                />
              )
            })}
          </Cell>
        </>
      )}
      {familyDetailsState.queueFamily.children.length > 0 && (
        <>
          {/* Row */}
          <Cell columns={12}>
            <H5>{t('children', 'Children')}</H5>
          </Cell>

          {/* Row */}
          <Cell columns={12}>
            {familyDetailsState.queueFamily.children.map((child) => {
              return (
                <PersonDetailsView
                  key={child.personId}
                  person={child}
                  buttonTemplates={buttonTemplates}
                  familyDetailsState={familyDetailsState}
                  familyDetailsActions={familyDetailsActions}
                />
              )
            })}
          </Cell>
        </>
      )}
      <Cell columns={12}>
        <RawData state={familyDetailsState} testId="familyDetailsState" />
      </Cell>
    </Grid>
  )
}

function PersonDetailsView({ person, buttonTemplates, familyDetailsState, familyDetailsActions }) {
  const { t } = useTranslation()
  const visitedCss = css`
    color: #c0285c;
  `
  const newTab = familyDetailsState.queueName.includes('HOLOCAUST') ? 'treePersonTab' : true
  return (
    <Grid key={person.personId}>
      {/* Column */}
      <Cell columns={8}>
        <Grid>
          {/* Row */}
          <Cell columns={4}>
            <InputGroup>
              <PersonLink
                personId={person.personId}
                personName={person.name}
                showId
                newTab={familyDetailsState.personLinkInNewTab}
              />
              <TreePersonLink personId={person.personId} newTab={newTab} treeIcon />
            </InputGroup>
          </Cell>
          <Cell columns={4}>
            {familyDetailsState.showViewButton && !person.isPrincipal && (
              <Row>
                <IconButton
                  Icon={PersonFamily}
                  title={t('queues.setAsPrincipal', 'Set as Principal')}
                  onClick={() => familyDetailsActions.lookForFamily(person.personId)}
                />
                <Button
                  key="view"
                  onClick={() => {
                    familyDetailsActions.lookForFamilyInNewTab(person.personId)
                    familyDetailsActions.updateViewButtonsVisited(person.personId)
                    // setVisited([...visited, person.personId])
                  }}
                >
                  <span css={familyDetailsState.viewButtonsVisited.includes(person.personId) && visitedCss}>
                    {t('action.view', 'View')}
                  </span>
                </Button>
              </Row>
            )}
          </Cell>

          <Cell columns={8}>
            <DataBlock heading={t('sourceCount', 'Sources: {sources}', { sources: person.sourceCount })} data="" />
          </Cell>

          {/* Row */}
          <OriginalAndStandardFactBlock heading={t('birth', 'Birth')} fact={person?.birth} columns={4} />

          {/* Row */}
          <OriginalAndStandardFactBlock heading={t('death', 'Death')} fact={person?.death} columns={4} />

          {/* Row */}
          <OriginalAndStandardFactBlock heading={t('burial', 'Burial')} fact={person?.burial} columns={4} />

          {/* Row */}
          <OriginalAndStandardFactBlock heading={t('marriage', 'Marriage')} fact={person?.marriage} columns={4} />

          {/* Row */}
          <LabelsBlock personLabels={person?.labels} columns={8} />
        </Grid>
      </Cell>

      {/* Column */}
      <Cell columns={4}>
        <Buttons
          person={person}
          buttonTemplates={buttonTemplates}
          familyDetailsState={familyDetailsState}
          familyDetailsActions={familyDetailsActions}
        />
      </Cell>
    </Grid>
  )
}

function Buttons({ person, buttonTemplates, familyDetailsActions }) {
  return (
    <Row wrap gutters="xs">
      {buttonTemplates.map((buttonTemplate) => {
        if (buttonTemplate.dialogModalComponent != null) {
          return (
            <div key={`${person.personId}${buttonTemplate.buttonText}`}>
              {familyDetailsActions.renderModalDialog(person, buttonTemplate.dialogModalComponent)}
            </div>
          )
        }

        return (
          <Button
            key={`${person.personId}${buttonTemplate.buttonText}`}
            onClick={() => familyDetailsActions.callButtonCallback(person, buttonTemplate.buttonCallback)}
          >
            {buttonTemplate.buttonText}
          </Button>
        )
      })}
    </Row>
  )
}
